<script lang="ts">
export default {
  name: 'CarrierView',
};
</script>

<script setup lang="ts">
import { carrierService, favoriteService } from '@/services/api';
import { Carrier } from '@/types/Carrier';
import { useRoute } from 'vue-router';
import { computed, onMounted, ref } from 'vue';
import PageProgress from '@/components/PageProgress.vue';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs';
import type { TabConfig } from '@/types/EntityTabs';
import { ComputedRef } from 'vue';

const route = useRoute();
const { getTabs } = useEntityTabs();

const loading = ref(true);
const carrier = ref({} as Carrier);
const favoriteProgress = ref(false);
let carrierTabs: ComputedRef<TabConfig[]> = computed(() => []);

async function toggleFavorite() {
  favoriteProgress.value = true;
  if (!carrier.value.favorite) {
    await favoriteService.remove('carrier', carrier.value.ID);
    carrier.value.favorite = false;
  } else {
    await favoriteService.create('carrier', carrier.value.ID);
    carrier.value.favorite = true;
  }
  favoriteProgress.value = false;
}

async function loadCarrier() {
  carrier.value = await carrierService.findById(route.params.id as string);
}

async function loadCarrierTabs() {
  try {
    carrierTabs = await getTabs('carrier', route.params.id as string);
  } catch (error) {
    console.error('Error loading carrier tabs:', error);
    carrierTabs = computed(() => []);
  }
}

onMounted(async () => {
  registerResizeItem({ name: 'carrier-header', selector: 'carrier-header' });
  registerResizeItem({ name: 'carrier-tabs', selector: 'carrier-tabs' });
  await loadCarrier();
  await loadCarrierTabs();
  loading.value = false;
});
</script>

<template>
  <div>
    <PageProgress v-if="loading" />
    <div v-else>
      <div id="carrier-header" class="inner">
        <div class="header">
          <h1>{{ carrier.name }}</h1>
          <FavoriteIndicator
            v-model="carrier.favorite"
            @update:model-value="toggleFavorite()"
            :loading="favoriteProgress" />
          <router-link
            v-if="carrier.rating"
            :to="`/carrier/${carrier.ID}/insurer-ratings`"
            data-test="rating-link">

            <div class="tw:flex tw:gap-1">
              <div class="label">AM BEST RATING:</div>
              <div class="value">{{ carrier.rating }}</div>
            </div>
          </router-link>
        </div>
      </div>

      <div class="tw:border-b tw:border-l-0 tw:border-r-0 tw:border-t tw:border-solid tw:border-gray-200">
        <v-tabs
          id="carrier-tabs"
          show-arrows
          bg-color="grey-lighten-4"
          class="tab-bar tw:uppercase"
          slide-color="primary"
          selected-class="tw:text-primary">
          <v-tab 
            v-for="tab in carrierTabs"
            :key="tab.key"
            :ripple="false" 
            class="tw:text-xs tw:text-gray-600"
            :to="tab.route"
          >
            {{ tab.label }}
          </v-tab>
        </v-tabs>
      </div>
      <router-view></router-view>
    </div>
  </div>
</template>

<style scoped>
.header {
  display: grid;
  grid-template-columns: auto auto 1fr;
  grid-column-gap: 1rem;
  align-items: center;
  align-content: center;
  justify-content: left;
}
</style>
